
//new POPUP FINAL
//src/ActionPopup.tsx
import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface ActionPopupProps {
  onClose: () => void; // Retained if parent needs to close it externally
  onConfirm: () => void; // Callback when the *final* step is confirmed
  // If the parent needs to control the initial step or reset it:
  // initialStep?: 'step1' | 'step2'; 
}

interface StepContentConfig {
  popupTitle: string;
  popupText: string;
  nextButtonText: string;
  illustrationUrl: string;
  showPreviousButton: boolean;
  activeDot: number;
  gradient: string;
  popupMarginRight: string;
  popupMarginTop: string;
  showTenantElements: boolean; // Present in both, so not optional here
  showDataSourceElements: boolean; // Present in both, so not optional here

  // DataSource specific properties - make these optional
  dataSourceArrowUrl?: string;
  dataSourceIconPath?: string;
  dataSourceBoxText?: string;
}

// Define content for each step
// TypeScript will now check these objects against StepContentConfig
const stepContents: { step1: StepContentConfig; step2: StepContentConfig } = {
  step1: {
    popupTitle: 'Add Tenant',
    popupText: 'View, Add, Switch tenant from here. Start adding your tenants.',
    nextButtonText: 'Next',
    illustrationUrl: '/image4.svg', // Illustration from Screen 1
    showTenantElements: true,
    showDataSourceElements: false, // Explicitly false for step 1
    showPreviousButton: false,
    activeDot: 1,
    gradient: 'linear-gradient(180deg, #D9E7FD 0%, #FFFFFF 100%)',
    popupMarginRight: '40%',
    popupMarginTop: '10%',
    // dataSource... properties are not needed for step1, so they are omitted (and are optional in the interface)
  },
  step2: {
    popupTitle: 'Add Data Source',
    popupText: 'View and add Data Sources from here. Start adding your Data Sources.',
    nextButtonText: 'Got It',
    illustrationUrl: '/image5.svg', // Placeholder for Screen 2 illustration
    showTenantElements: false, // Explicitly false for step 2
    showDataSourceElements: true, // Show these elements for step 2
    dataSourceArrowUrl: '/add-tenant-arrow.svg', // Placeholder for Screen 2 arrow - REPLACE WITH YOUR ASSET
    dataSourceIconPath: '/icons/DataSources.svg', // Placeholder for Screen 2 icon - REPLACE WITH YOUR ASSET
    dataSourceBoxText: 'Data Sources', // This was the missing property
    showPreviousButton: true,
    activeDot: 2,
    gradient: 'linear-gradient(180deg, #D9E7FD 0%, #FFFFFF 100%)', // Example different gradient
    popupMarginRight: '40%', // More centered for step 2
    popupMarginTop: '5%',  // Slightly higher for step 2
  },
};

const ActionPopup: React.FC<ActionPopupProps> = ({ onConfirm }) => {
  const theme = useTheme();
  const [currentStepKey, setCurrentStepKey] = useState<'step1' | 'step2'>('step1');

  const addtenantarrow = '/add-tenant-arrow.svg';
  const addNewTenantIconPath = '/icons/building-line.svg';

  // --- Explicitly type currentStepData with our new interface ---
  const currentStepData: StepContentConfig = stepContents[currentStepKey];

  const handleNextOrConfirm = () => {
    if (currentStepKey === 'step1') {
      setCurrentStepKey('step2');
    } else if (currentStepKey === 'step2') {
      onConfirm();
    }
  };

  const handlePrevious = () => {
    if (currentStepKey === 'step2') {
      setCurrentStepKey('step1');
    }
  };

  return (
    // Backdrop
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        zIndex: 1200,
      }}
    >
      {/* Conditional "Add New Tenant" elements (ONLY for Step 1) */}
      {currentStepData.showTenantElements && (
        <>
          <img
            src={process.env.PUBLIC_URL + addtenantarrow}
            alt="Add tenant arrow"
            style={{ position: 'absolute', width: '150px', marginRight: '75%', marginTop: '40%', zIndex: 1201 }}
          />
          <Box sx={{
            position: 'fixed',
            bottom: theme.spacing(2),
            left: theme.spacing(3),
            zIndex: 1301, 
            p: 0,
          }}>
            <Box
              sx={{
                width: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Inter, Arial, sans-serif', backgroundColor: 'white', color: '#0064FF',
                textTransform: 'none', fontWeight: 500, fontSize: '0.875rem',
                borderRadius: '8px', py: 1.25, px: 2.75,
              }}
            >
              <img src={process.env.PUBLIC_URL + addNewTenantIconPath} alt="" style={{ width: '20px', height: '20px', marginRight: theme.spacing(1) }} />
              <Typography component="span" sx={{ color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
                Add New Tenant
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Conditional "Data Sources" elements (ONLY for Step 2) */}
      {currentStepData.showDataSourceElements && currentStepData.dataSourceArrowUrl && currentStepData.dataSourceIconPath && (
        <>
          <img
            src={process.env.PUBLIC_URL + currentStepData.dataSourceArrowUrl}
            alt="Data source arrow"
            style={{
              position: 'absolute',
              width: '130px',
              left: theme.spacing(18), // Positioned more to the left
              top: '30%',
              zIndex: 1201,
              transform: 'scaleX(-1) rotate(180deg)'
            }}
          />
          <Box sx={{
            position: 'fixed',
            top: '25%', 
            left: theme.spacing(3),
            zIndex: 1301,
            p: 0,
          }}>
            <Box
              sx={{
                width: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Inter, Arial, sans-serif', backgroundColor: 'white', color: theme.palette.text.primary,
                textTransform: 'none', fontWeight: 400, fontSize: '0.875rem',
                borderRadius: '8px', py: 1.25, px: 2.75,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', 
              }}
            >
              <img src={process.env.PUBLIC_URL + currentStepData.dataSourceIconPath} alt="" style={{ width: '20px', height: '20px', marginRight: theme.spacing(1) }} />
              <Typography component="span" sx={{ color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
                {/* Accessing optional property, guarded by showDataSourceElements */}
                {currentStepData.dataSourceBoxText}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Popup Card */}
      <Box
        sx={{
          p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
          borderRadius: '16px', width: '350px', height: '390px',
          marginRight: currentStepData.popupMarginRight,
          marginTop: currentStepData.popupMarginTop,
          bgcolor: '#FFFFFF', boxShadow: theme.shadows[5],
          position: 'relative', overflow: 'hidden', zIndex: 1300,
        }}
      >
        {/* Full-Width Gradient Top Section */}
        <Box
          sx={{
            width: '100%', minHeight: '200px',
            borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
            background: currentStepData.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: 3, pt: currentStepKey === 'step1' ? 3 : 4,
            boxSizing: 'border-box',
            position: 'relative', // IMPORTANT: Allows absolute positioning of children
            overflow: 'hidden', // IMPORTANT: Keeps the fade within the rounded corners
          }}
          >
        <img
          src={process.env.PUBLIC_URL + currentStepData.illustrationUrl}
          alt="Illustration"
          style={{
            width: currentStepKey === 'step1' ? '500px' : '500px',
            height: currentStepKey === 'step1' ? '150px' : '150px',
            marginLeft: currentStepKey === 'step1' ? '35%' : '35%',
          }}
          onError={(e) => {
            console.error("IMAGE LOAD ERROR! Path:", currentStepData.illustrationUrl);
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        {/* Fade Overlay at the bottom */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '60px', // Adjust the height of the fade area as needed
            // Gradient fades from fully transparent to opaque background color
            background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0)} 0%, ${theme.palette.background.paper} 90%)`,
            pointerEvents: 'none', // Ensures it doesn't block interactions
            zIndex: 1, // Ensures the fade overlay is on top of the image if they overlap
            // Apply bottom border radius to match the parent's if the fade height allows it
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        />
      </Box>

        {/* Bottom Section (Text and Button) */}
        <Box sx={{ p: { xs: 3, sm: 4 }, pt: 3, textAlign: 'left', width: '100%', boxSizing: 'border-box' }}>
          <Typography sx={{ fontSize: '17px', fontWeight: 700, mb: 1, fontFamily: 'Inter' }}>
            {currentStepData.popupTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', mb: 3, fontFamily: 'Inter', minHeight: '40px' }}>
            {currentStepData.popupText}
          </Typography>

          {/* Pagination & Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: currentStepData.activeDot === 1 ? '#000000' : '#E0E0E0' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: currentStepData.activeDot === 2 ? '#000000' : '#E0E0E0' }} />
            </Box>
            <Box>
              {currentStepData.showPreviousButton && (
                <Button
                  type="button" variant="text" onClick={handlePrevious}
                  sx={{ fontFamily: 'Inter', py: 1.25, px: 2, mr: 1, textTransform: 'none', border: '1.2px solid black', borderRadius: '10px', color: theme.palette.text.secondary, width: '120px', height: '44px' }}
                >
                  Previous
                </Button>
              )}
              <Button
                type="button" variant="contained" onClick={handleNextOrConfirm}
                sx={{
                  fontFamily: 'Inter', py: 1.25, px: 4, backgroundColor: '#0064FF',
                  '&:hover': { backgroundColor: alpha('#0064FF', 0.85) },
                  textTransform: 'none', borderRadius: '8px',
                }}
              >
                {currentStepData.nextButtonText}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ActionPopup;

